import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import AOS from 'aos'
import CustomAgGrid from '../../../../../components/CustomAgGrid'

import Text from '../../../../../elements/Typo/Text'
import { AgGridReact } from 'ag-grid-react'
import { IHeaderParams } from 'ag-grid-community'
import { ErrorResponse, SuccessResponse } from '../../../../../store/api/apiTypes'
import toast from 'react-hot-toast'
import DataTableLoader from '../../../../../components/DataTableLoader'
import { useKpiApi } from '../../../../../store/api/hooks/KpiApiHook'

import Loader from '../../../../../components/loader'
import { ADVANCE_CONFIG_OPTION } from '../../../../../_common/constant'
import { CellStyle, impactColumn } from '../../../../../_common/experimentTableColumnDef'
import ColumnHeader from '../../../../../components/Table/columnHeader'
import { getBackgroundColor, getFormattedPercentage, getIcon, getTextColorClass } from '../../../../../components/Table/utils'
import i18n from '../../../../../i18n'
import { EXPERIMENT_PROGRESS_STATUS } from '../../../../../_config'

export type KpiImpactI = {
    KPI: string
    Impact: number | null
    Uplift: number | null
    methodology?: string | null
    experiment_status?: string | null
}
interface Props {
    experimentDetails: any
    selectedGroup: string | null
    selectedOptionConfig?: string | null
    selectedKPI?: any
    selectedDateRange: any[]
    toDownloadAsReport?: boolean
    reportError?: (x: boolean) => void
    setKpiImpactsData: (data: KpiImpactI[]) => void
}

const CustomKpiHeader = (props: IHeaderParams) => {
    return (
        <Text size="sm" className="font-semibold font-[#4C4C50]">
            {props.displayName}
        </Text>
    )
}

const KPIImpactsSection: React.FC<Props> = ({ setKpiImpactsData, experimentDetails, selectedGroup, selectedOptionConfig, selectedKPI, selectedDateRange, toDownloadAsReport = false, reportError }) => {
    const { t } = useTranslation()
    const kpiGridRef = useRef<AgGridReact>(null)
    const { getKpiImpacts } = useKpiApi()

    const [selectedDidMethodoly, setSelectedDidMethodoly] = useState<boolean>(false)
    const [kpiImpacts, setKpiImpacts] = useState<KpiImpactI[]>([])
    const [kpiTableLoading, setKpiTableLoading] = useState<boolean>(false)
    const [selectedKpiStatus, setSelectedKpiStatus] = useState<string | null>(null)

    const _handleFetchKpiImpacts = (group_name: string) => {
        setKpiTableLoading(true)
        const params: any = {
            literal: experimentDetails.experiment_literal,
            group_name,
            methodology: selectedOptionConfig,
            kpiname: selectedKPI?.value || null,
        }

        if (selectedDateRange.length == 2) {
            params.measurement_start_date = selectedDateRange[0]
            params.measurement_end_date = selectedDateRange[1]
            params.is_slice_and_dice = true
        } else {
            params.measurement_start_date = null
            params.measurement_end_date = null
            params.is_slice_and_dice = false
        }

        getKpiImpacts(
            params,
            (resp: SuccessResponse<KpiImpactI[]>) => {
                const kpiImpactAr: KpiImpactI[] = resp.data.length > 2 ? [] : new Array(3 - resp.data.length).fill({ KPI: '-', Impact: null, Uplift: null })
                setSelectedKpiStatus(resp?.data?.[0]?.experiment_status || null)
                setKpiImpacts([...resp.data, ...kpiImpactAr])
                const isDid = resp?.data?.[0]?.methodology?.toLowerCase() === ADVANCE_CONFIG_OPTION.DID.value
                setSelectedDidMethodoly(isDid)
                setKpiTableLoading(false)
                setKpiImpactsData([...resp.data, ...kpiImpactAr])
            },
            (error: ErrorResponse<any>) => {
                setKpiTableLoading(false)
                if (reportError) {
                    reportError(true)
                }
                toast.error(error?.message || t('Something went wrong'))
            },
        )
    }

    const [columnDefs, setColumnDefs] = useState<any[]>([])

    useEffect(() => {
        if (experimentDetails?.experiment_literal && selectedGroup) {
            _handleFetchKpiImpacts(selectedGroup)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experimentDetails, selectedGroup, selectedOptionConfig, selectedKPI, selectedDateRange])

    const cellKPIRenderer = (params: any) => {
        return (
            <Text size="xs" className="font-semibold items-center truncate" title={params.data?.KPI ?? '-'}>
                {params.data?.KPI ?? '-'}
            </Text>
        )
    }

    const upliftCellRenderer = (params: any) => {
        return Number.isNaN(params.value) ? (
            <Text size="13" className={`py-1 text-base-content/60`}>
                {params.value}
            </Text>
        ) : (
            <div
                className="flex items-center justify-center rounded-[1.75rem] px-2 font-medium"
                style={selectedKpiStatus == EXPERIMENT_PROGRESS_STATUS.NOIMPACT ? {} : { backgroundColor: getBackgroundColor(params.value, selectedKPI?.trends_as == 'positive_downword', params.data?.experiment_status) }}
            >
                {' '}
                {getIcon(params.value, selectedKPI?.trends_as == 'positive_downword', params.data?.experiment_status)}
                <Text size="13" className={`py-1 text-base-content/60 ${getTextColorClass(params.value)}`}>
                    {getFormattedPercentage(params.value)}
                </Text>
            </div>
        )
    }

    useEffect(() => {
        setColumnDefs([
            {
                headerName: t('KPI'),
                field: 'KPI',
                filter: false,
                suppressHeaderMenuButton: true,
                sortable: false,
                minWidth: 100,
                flex: 1,
                cellStyle: CellStyle,
                headerComponent: ColumnHeader,
                cellRenderer: cellKPIRenderer,
            },
            { ...impactColumn, field: 'Impact', headerName: selectedDidMethodoly ? t('Average KPI Impact') : t('Impact') },
            {
                headerName: t('Uplift'),
                field: 'Uplift',
                filter: false,
                sortable: false,
                flex: 10,
                headerComponent: CustomKpiHeader,
                headerClass: 'centered-header',
                cellStyle: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
                cellRenderer: upliftCellRenderer,
            },
        ])
    }, [kpiImpacts, selectedDidMethodoly, t])

    useEffect(() => {
        const params: any = {}

        if (toDownloadAsReport) {
            params['disable'] = true // Animation disabled
        } else {
            params['once'] = false // Animation occurs every time you scroll to the element
        }

        AOS.init(params)
        AOS.refresh()
    }, [])

    return (
        <div data-testid="kpi-impacts-section" className="col-span-3 md:col-span-1 bg-base-100 rounded-[0.45rem] shadow-1 mb-2" data-aos="zoom-in" data-aos-delay="200">
            <div className="px-3 py-3 roushared with:nded-[0.7rem]">
                <Text size="md" className="font-semibold">
                    {t('KPI Impacts')}
                </Text>
                {kpiTableLoading ? (
                    <Loader />
                ) : (
                    <div className="mt-2">
                        <CustomAgGrid
                            key={'kpiImpacts_' + i18n.language}
                            gridRef={kpiGridRef}
                            rowData={kpiTableLoading ? null : kpiImpacts}
                            columnDefs={columnDefs}
                            theme="light"
                            extraThemeProps={{ headerRowBorder: false, headerColumnBorder: true }}
                            pagination={false}
                            style={{ height: 160, width: '100%' }}
                            gridId="summmyGrid"
                            rowHeight={40}
                            alternativeRowColor={'#E5B61112'}
                            loadingOverlayComponent={DataTableLoader}
                            loadingOverlayComponentParams={{ className: 'h-6 w-6' }}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
export default KPIImpactsSection
